"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { extractDocIssueTargets } from "@/lib/docIssueReferences";
import { loadWorkspaceIssueReference, type WorkspaceIssueReferenceTarget } from "@/lib/data/workspaceIssueReference";
import type { Doc, Issue, Team } from "@/lib/types";

const MAX_TARGETS = 100;
const REQUEST_DEADLINE_MS = 30_000;
type Status = "ready" | "loading" | "error";

function workspacePrefixes(teams: readonly Team[], workspaceId: string): string {
  return JSON.stringify([...new Set(teams.filter((team) => team.workspaceId === workspaceId)
    .map((team) => team.key.toUpperCase()).filter((key) => /^[A-Z][A-Z0-9]*$/.test(key)))].sort());
}

/** Fetch explicit forward references missing from the capped bootstrap. The
 * caller may combine these local extras with newer canonical store rows. */
export function useDocIssueReferences({ doc, accessibleIssues, workspaceSlug, canonicalOrigin, currentUserId }: {
  doc: Pick<Doc, "id" | "workspaceId" | "content"> | null;
  accessibleIssues: readonly Issue[];
  workspaceSlug: string;
  canonicalOrigin?: string;
  currentUserId: string;
}): { issues: Issue[]; status: Status; retry: () => void } {
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);
  const activeUserId = useStore((state) => state.currentUserId);
  const teams = useStore((state) => state.teams);
  const teamPrefixKey = workspacePrefixes(teams, doc?.workspaceId ?? "");
  const [attempt, setAttempt] = useState(0);
  const extracted = useMemo(() => doc ? extractDocIssueTargets({ doc, workspaceSlug, canonicalOrigin,
    teamPrefixes: JSON.parse(teamPrefixKey) as string[] })
    : { issueIds: [], identifiers: [] }, [doc?.content, workspaceSlug, canonicalOrigin, teamPrefixKey]);
  const inWorkspace = accessibleIssues.filter((issue) => issue.workspaceId === doc?.workspaceId);
  const knownIds = new Set(inWorkspace.map((issue) => issue.id.toLowerCase()));
  const knownIdentifiers = new Set(inWorkspace.map((issue) => issue.identifier.toUpperCase()));
  const missing: WorkspaceIssueReferenceTarget[] = [
    ...extracted.issueIds.filter((id) => !knownIds.has(id)).map((value) => ({ kind: "id" as const, value })),
    ...extracted.identifiers.filter((identifier) => !knownIdentifiers.has(identifier)).map((value) => ({ kind: "identifier" as const, value })),
  ];
  const missingKey = JSON.stringify(missing);
  const exceedsLimit = extracted.issueIds.length + extracted.identifiers.length > MAX_TARGETS;
  const context = useMemo(() => ({
    docId: doc?.id ?? null,
    workspaceId: doc?.workspaceId ?? "",
    userId: currentUserId,
    teamPrefixKey,
    targets: JSON.parse(missingKey) as WorkspaceIssueReferenceTarget[],
    invalid: false,
    allowed: !!doc && !!currentUserId && activeWorkspaceId === doc.workspaceId && activeUserId === currentUserId && !exceedsLimit,
  }), [doc?.id, doc?.workspaceId, doc?.content, workspaceSlug, canonicalOrigin,
    currentUserId, activeWorkspaceId, activeUserId, exceedsLimit, missingKey, teamPrefixKey, attempt]);
  const [snapshot, setSnapshot] = useState<{ owner: typeof context | null; issues: Issue[]; status: Status }>({ owner: null, issues: [], status: "ready" });

  useEffect(() => {
    if (!context.allowed || !context.targets.length) return;
    const abort = new AbortController();
    let disposed = false;
    const inScope = () => {
      const state = useStore.getState();
      return state.activeWorkspaceId === context.workspaceId && state.currentUserId === context.userId
        && workspacePrefixes(state.teams, context.workspaceId) === context.teamPrefixKey;
    };
    const current = () => !disposed && !context.invalid && !abort.signal.aborted && inScope();
    const invalidate = () => {
      if (disposed) return;
      context.invalid = true;
      abort.abort();
      setSnapshot({ owner: context, issues: [], status: "error" });
    };
    // Sticky invalidation protects even A → B → A transitions batched before a render.
    const unsubscribe = useStore.subscribe(() => { if (!inScope()) invalidate(); });
    const client = createClient();
    const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
      if (session?.user.id !== context.userId) invalidate();
    });
    const deadline = setTimeout(invalidate, REQUEST_DEADLINE_MS);
    setSnapshot({ owner: context, issues: [], status: "loading" });
    void (async () => {
      let next = 0;
      const found = new Map<string, Issue>();
      const worker = async () => {
        while (current() && next < context.targets.length) {
          const target = context.targets[next++];
          const result = await loadWorkspaceIssueReference({ workspaceId: context.workspaceId, userId: context.userId, target, signal: abort.signal });
          if (!current()) return;
          if (result.status === "error") { invalidate(); return; }
          if (result.status === "found" && result.issue.workspaceId === context.workspaceId) found.set(result.issue.id, result.issue);
        }
      };
      try {
        await Promise.all(Array.from({ length: Math.min(4, context.targets.length) }, worker));
        if (current()) setSnapshot({ owner: context, issues: [...found.values()], status: "ready" });
      } catch {
        if (current()) invalidate();
      } finally {
        clearTimeout(deadline);
      }
    })();
    return () => {
      disposed = true;
      abort.abort();
      clearTimeout(deadline);
      unsubscribe();
      subscription.unsubscribe();
    };
  }, [context]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  if (!doc) return { issues: [], status: "ready", retry };
  if (!context.allowed || context.invalid) return { issues: [], status: "error", retry };
  if (!context.targets.length) return { issues: [], status: "ready", retry };
  return snapshot.owner === context ? { issues: snapshot.issues, status: snapshot.status, retry }
    : { issues: [], status: "loading", retry };
}
